export type NoticeStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED";

export interface NoticeStep {
  id: string;
  description: string;
  position: number;
  dueDate: string | null;
  isCompleted: boolean;
  completedAt: string | null;
}

export interface NoticeDefinitionOption {
  id: string;
  code: string;
  title: string;
  severity: string;
  category: string;
  recommendedAction: string;
}

export interface Notice {
  id: string;
  client: string;
  clientName: string;
  case: string | null;
  noticeDefinition: string;
  noticeCode: string;
  noticeTitle: string;
  severity: string;
  category: string;
  recommendedAction: string;
  receivedOn: string;
  responseDeadline: string | null;
  daysUntilDeadline: number | null;
  status: NoticeStatus;
  statusDisplay: string;
  taxYear: string;
  notes: string;
  assignedPractitioner: string | null;
  assignedPractitionerName: string;
  document: string | null;
  steps: NoticeStep[];
  openStepCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface NoticePayload {
  notice_definition: string;
  received_on: string;
  response_deadline?: string | null;
  status?: NoticeStatus;
  tax_year?: string;
  notes?: string;
  assigned_practitioner?: string | null;
  document?: string | null;
}

export interface NoticeStepPayload {
  description?: string;
  due_date?: string | null;
  is_completed?: boolean;
  position?: number;
}

export const NOTICE_STATUS_OPTIONS: { value: NoticeStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
];

// --- firm notice step templates (Settings › Notice Templates) ---------------

export interface NoticeTemplateStep {
  id: string;
  description: string;
  link: string;
  position: number;
}

export interface NoticeTemplate {
  /** null until the firm saves its first guidance/step for this code. */
  id: string | null;
  noticeDefinition: string;
  code: string;
  title: string;
  severity: string;
  category: string;
  description: string;
  recommendedAction: string;
  guidance: string;
  steps: NoticeTemplateStep[];
}

export interface TemplateStepPayload {
  description?: string;
  link?: string;
}

// --- advisor ----------------------------------------------------------------

export interface IAPaymentOption {
  label: string;
  months: number | null;
  monthlyPaymentCents: number | null;
  requiresFinancials: boolean;
}

export interface AdvisorRecommendation {
  resolutionType: string | null;
  terminalPhase: string;
  blocked: boolean;
  opensCase: boolean;
  suggestedNoticeCode: string | null;
  owedAmountCents: number | null;
  owedRange: number | null;
  iaPaymentOptions: IAPaymentOption[];
  warnings: string[];
  messages: string[];
}

/** Assembled advisor answers posted to /notices/advisor/recommend/. Mirrors
 * the backend AdvisorInputSerializer (snake_case on the wire). */
export interface AdvisorInput {
  intent: "notice" | "abate" | "alternative";
  notice_code?: string;
  abate_basis?: "first_time" | "reasonable_cause";
  client_type?: "I" | "B";
  filed?: boolean;
  bankrupt?: boolean;
  payments?: boolean;
  entered?: boolean;
  total_tax?: string;
  bus_active?: boolean;
  bus_soleprop?: boolean;
  bus_payroll?: boolean;
  tax_due_amt?: string;
  tax_due_range?: number;
  solution_choice?: string;
}
